<template>
  <div class="md-editor" :class="{ 'md-editor--fullscreen': fullscreen }">
    <div class="md-editor__toolbar">
      <div class="md-editor__toolbar-group">
        <button
          type="button"
          class="md-editor__btn"
          title="Bold"
          @click="wrapSelection('**', '**', 'bold text')"
        >
          <b>B</b>
        </button>
        <button
          type="button"
          class="md-editor__btn"
          title="Italic"
          @click="wrapSelection('*', '*', 'italic text')"
        >
          <i>I</i>
        </button>
        <button
          type="button"
          class="md-editor__btn"
          title="Heading"
          @click="toggleLinePrefix('## ')"
        >
          H
        </button>
        <button
          type="button"
          class="md-editor__btn"
          title="Link"
          @click="wrapSelection('[', '](https://)', 'link text')"
        >
          Link
        </button>
        <button
          type="button"
          class="md-editor__btn"
          title="Bullet list"
          @click="toggleLinePrefix('- ')"
        >
          List
        </button>
        <button
          type="button"
          class="md-editor__btn"
          title="Numbered list"
          @click="toggleLinePrefix('1. ')"
        >
          1.
        </button>
        <button
          type="button"
          class="md-editor__btn"
          title="Insert table"
          @click="insertBlock(tableTemplate)"
        >
          Table
        </button>
      </div>
      <div class="md-editor__toolbar-group">
        <button
          type="button"
          class="md-editor__btn"
          title="Info box block"
          @click="insertBlock(infoBoxTemplate)"
        >
          Info
        </button>
        <button
          type="button"
          class="md-editor__btn"
          title="Centered button block"
          @click="insertBlock(buttonTemplate)"
        >
          Button
        </button>
      </div>
      <div class="md-editor__toolbar-group">
        <select
          class="md-editor__select"
          v-model="selectedVariable"
          @change="insertVariable"
        >
          <option value="" disabled>Insert variable...</option>
          <option v-for="name in variableNames" :key="name" :value="name">
            {{ name }}
          </option>
        </select>
      </div>
      <div class="md-editor__toolbar-group md-editor__toolbar-group--right">
        <button
          type="button"
          class="md-editor__btn"
          :class="{ active: mode === 'write' }"
          @click="mode = 'write'"
        >
          Write
        </button>
        <button
          type="button"
          class="md-editor__btn"
          :class="{ active: mode === 'preview' }"
          @click="mode = 'preview'"
        >
          Preview
        </button>
        <button
          type="button"
          class="md-editor__btn"
          :class="{ active: mode === 'split' }"
          @click="mode = 'split'"
        >
          Split
        </button>
        <button type="button" class="md-editor__btn" @click="toggleFullscreen">
          {{ fullscreen ? "Exit fullscreen" : "Fullscreen" }}
        </button>
      </div>
    </div>
    <div ref="body" class="md-editor__body">
      <textarea
        v-show="mode !== 'preview'"
        ref="textarea"
        class="md-editor__textarea"
        :value="modelValue"
        :placeholder="placeholder"
        @input="onInput"
        @keydown="onKeydown"
        @blur="closeSuggestions"
      ></textarea>
      <div
        v-show="mode !== 'write'"
        class="md-editor__preview"
        v-html="previewHtml"
      ></div>
      <div
        v-if="showSuggestions && filteredVariables.length > 0"
        ref="suggestions"
        class="md-editor__suggestions"
        :style="{ left: suggestionLeft + 'px', top: suggestionTop + 'px' }"
      >
        <ul class="md-editor__suggestions-list">
          <li
            v-for="(variable, index) in filteredVariables"
            :key="variable"
            class="md-editor__suggestion"
            :class="{ active: index === suggestionIndex }"
            @mousedown.prevent="insertSuggestionAt(index)"
            @mouseenter="suggestionIndex = index"
          >
            {{ variable }}
          </li>
        </ul>
        <div class="md-editor__suggestions-hint">
          Use ↑ ↓ to navigate, Enter to insert, Esc to close
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MarkdownIt from "markdown-it";
import markdownItContainer from "markdown-it-container";
import { alignTables } from "../../modules/markdownTable";

const SPECIAL_VARIABLES = [
  "orderItems",
  "addedItems",
  "returnItems",
  "deletedItems",
  "pin",
  "deliveryStatus",
];

function variableRule(state, silent) {
  const src = state.src;
  const start = state.pos;
  if (src.charCodeAt(start) !== 0x7b || src.charCodeAt(start + 1) !== 0x7b) {
    return false;
  }
  let pos = start + 2;
  let name = "";
  while (pos < state.posMax) {
    if (src.charCodeAt(pos) === 0x7d && src.charCodeAt(pos + 1) === 0x7d) {
      name = src.slice(start + 2, pos);
      break;
    }
    pos++;
  }
  if (name === "" || /[\s{}]/.test(name)) {
    return false;
  }
  if (!silent) {
    const token = state.push("variable", "span", 0);
    token.content = name;
  }
  state.pos = pos + 2;
  return true;
}

function variableRenderer(tokens, idx) {
  const name = tokens[idx].content;
  const text = "{{" + name + "}}";
  if (SPECIAL_VARIABLES.indexOf(name) !== -1) {
    return `<span class="md-variable md-variable--special">${text}</span>`;
  }
  return `<span class="md-variable" title="Variable">${text}</span>`;
}

const md = new MarkdownIt({
  html: true,
  linkify: true,
  breaks: true,
});

md.inline.ruler.before("text", "variable", variableRule);
md.renderer.rules.variable = variableRenderer;

md.use(markdownItContainer, "info", {
  validate: (params) => params.trim().split(" ", 2)[0] === "info",
  render: (tokens, idx) =>
    tokens[idx].nesting === 1
      ? '<div class="md-container md-container--info">'
      : "</div>",
});

md.use(markdownItContainer, "justify", {
  validate: (params) => params.trim().split(" ", 2)[0] === "justify",
  render: (tokens, idx) =>
    tokens[idx].nesting === 1
      ? '<div class="md-container md-container--justify">'
      : "</div>",
});

export default {
  name: "MarkdownEditor",
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    variables: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      mode: "split",
      fullscreen: false,
      selectedVariable: "",
      lastSynced: null,
      showSuggestions: false,
      suggestionIndex: 0,
      suggestionFilter: "",
      suggestionStart: 0,
      suggestionLeft: 0,
      suggestionTop: 0,
      tableTemplate:
        "| Column | Column | Column |\n| --- | --- | --- |\n| value 1 | value 2 | value 3 |",
      infoBoxTemplate: "::: info box\n**Important**: text\n:::",
      buttonTemplate:
        "::: justify center\n[Button text](https://example.com)\n:::",
    };
  },
  computed: {
    variableNames() {
      return (this.variables || [])
        .map((v) => (typeof v === "string" ? v : v.variable))
        .filter(Boolean);
    },
    filteredVariables() {
      if (!this.suggestionFilter) return this.variableNames;
      return this.variableNames.filter((v) =>
        v.toLowerCase().includes(this.suggestionFilter)
      );
    },
    previewHtml() {
      return md.render(this.modelValue || "");
    },
  },
  watch: {
    mode() {
      this.closeSuggestions();
    },
    modelValue(value) {
      this.syncAlign(value);
    },
  },
  mounted() {
    this.syncAlign(this.modelValue);
  },
  methods: {
    emitValue(next) {
      this.lastSynced = next;
      this.$emit("update:modelValue", next);
    },
    syncAlign(value) {
      if (value === this.lastSynced) return;
      const aligned = alignTables(value);
      this.lastSynced = aligned;
      if (aligned !== value) {
        this.$emit("update:modelValue", aligned);
      }
    },
    onInput(event) {
      const value = event.target.value;
      this.emitValue(value);
      this.updateSuggestions(event.target, value);
    },
    onKeydown(event) {
      if (!this.showSuggestions || this.filteredVariables.length === 0) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        this.suggestionIndex =
          (this.suggestionIndex + 1) % this.filteredVariables.length;
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        this.suggestionIndex =
          (this.suggestionIndex - 1 + this.filteredVariables.length) %
          this.filteredVariables.length;
      } else if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        this.insertSuggestion();
      } else if (event.key === "Escape") {
        event.preventDefault();
        this.closeSuggestions();
      }
    },
    updateSuggestions(textarea, value) {
      if (!textarea) return;
      const source = value !== undefined ? value : this.modelValue;
      const cursor = textarea.selectionStart;
      const before = source.slice(0, cursor);
      const lastOpen = before.lastIndexOf("{{");
      if (lastOpen === -1) {
        this.closeSuggestions();
        return;
      }
      const lastClose = before.lastIndexOf("}}");
      if (lastClose > lastOpen) {
        this.closeSuggestions();
        return;
      }
      const filter = before.slice(lastOpen + 2).toLowerCase();
      const matches = this.variableNames.filter((v) =>
        v.toLowerCase().includes(filter)
      );
      if (matches.length === 0) {
        this.closeSuggestions();
        return;
      }
      this.suggestionStart = lastOpen;
      this.suggestionFilter = filter;
      this.suggestionIndex = 0;
      this.showSuggestions = true;
      const coords = this.getCaretCoordinates(textarea, cursor);
      const textareaRect = textarea.getBoundingClientRect();
      const bodyRect = this.$refs.body.getBoundingClientRect();
      this.suggestionLeft =
        textareaRect.left - bodyRect.left + coords.left - textarea.scrollLeft;
      this.suggestionTop =
        textareaRect.top - bodyRect.top + coords.top - textarea.scrollTop;
      this.$nextTick(() => {
        const list = this.$refs.suggestions;
        if (!list) return;
        const listRect = list.getBoundingClientRect();
        const padding = 4;
        let left = this.suggestionLeft;
        let top = this.suggestionTop;
        if (left < padding) left = padding;
        if (left + listRect.width > bodyRect.width - padding) {
          left = Math.max(padding, bodyRect.width - listRect.width - padding);
        }
        if (
          top + listRect.height > bodyRect.height - padding &&
          top > listRect.height
        ) {
          top = Math.max(padding, top - listRect.height - padding);
        }
        this.suggestionLeft = left;
        this.suggestionTop = top;
      });
    },
    getCaretCoordinates(textarea, position) {
      const computed = getComputedStyle(textarea);
      const styleProps = [
        "borderTopWidth",
        "borderRightWidth",
        "borderBottomWidth",
        "borderLeftWidth",
        "boxSizing",
        "fontFamily",
        "fontSize",
        "fontStyle",
        "fontWeight",
        "letterSpacing",
        "lineHeight",
        "paddingTop",
        "paddingRight",
        "paddingBottom",
        "paddingLeft",
        "textTransform",
        "wordSpacing",
      ];
      const div = document.createElement("div");
      div.setAttribute(
        "style",
        "position:absolute;top:0;left:0;z-index:-1;visibility:hidden;white-space:pre-wrap;word-wrap:break-word;overflow-wrap:break-word;"
      );
      styleProps.forEach((p) => {
        div.style[p] = computed[p];
      });
      div.textContent = textarea.value.slice(0, position);
      const span = document.createElement("span");
      span.textContent = textarea.value.slice(position) || ".";
      div.appendChild(span);
      document.body.appendChild(div);
      const height = parseInt(computed.lineHeight, 10);
      const coordinates = {
        top: span.offsetTop + parseInt(computed.borderTopWidth, 10),
        left: span.offsetLeft + parseInt(computed.borderLeftWidth, 10),
        height: isNaN(height) ? 16 : height,
      };
      document.body.removeChild(div);
      return coordinates;
    },
    closeSuggestions() {
      this.showSuggestions = false;
      this.suggestionFilter = "";
    },
    insertSuggestionAt(index) {
      this.suggestionIndex = index;
      this.insertSuggestion();
    },
    insertSuggestion() {
      const el = this.$refs.textarea;
      const variable = this.filteredVariables[this.suggestionIndex];
      if (!el || !variable) return;
      const cursor = el.selectionStart;
      const insertText = "{{" + variable + "}}";
      const start = this.suggestionStart;
      const next =
        this.modelValue.slice(0, start) +
        insertText +
        this.modelValue.slice(cursor);
      this.emitValue(next);
      this.closeSuggestions();
      this.$nextTick(() => {
        el.focus();
        const pos = start + insertText.length;
        el.selectionStart = pos;
        el.selectionEnd = pos;
      });
    },
    insertAtCursor(text) {
      const el = this.$refs.textarea;
      if (!el) {
        this.emitValue(this.modelValue + text);
        return;
      }
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next =
        this.modelValue.slice(0, start) + text + this.modelValue.slice(end);
      this.emitValue(next);
      this.$nextTick(() => {
        el.focus();
        el.selectionStart = start + text.length;
        el.selectionEnd = start + text.length;
      });
    },
    insertBlock(text) {
      const el = this.$refs.textarea;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const block = "\n" + text + "\n";
      const next =
        this.modelValue.slice(0, start) + block + this.modelValue.slice(end);
      this.emitValue(alignTables(next));
      this.$nextTick(() => {
        el.focus();
        el.selectionStart = start + block.length;
        el.selectionEnd = start + block.length;
      });
    },
    wrapSelection(before, after, placeholder) {
      const el = this.$refs.textarea;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const selected = this.modelValue.slice(start, end) || placeholder || "";
      const next =
        this.modelValue.slice(0, start) +
        before +
        selected +
        after +
        this.modelValue.slice(end);
      this.emitValue(next);
      this.$nextTick(() => {
        el.focus();
        el.selectionStart = start + before.length;
        el.selectionEnd = start + before.length + selected.length;
      });
    },
    toggleLinePrefix(prefix) {
      const el = this.$refs.textarea;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const text = this.modelValue;
      const before = text.slice(0, start);
      const lineStart = before.lastIndexOf("\n") + 1;
      const after = text.slice(end);
      const nextLine = after.indexOf("\n");
      const lineEnd = nextLine === -1 ? text.length : end + nextLine;
      const line = text.slice(lineStart, lineEnd);
      let next;
      let delta;
      if (line.startsWith(prefix)) {
        next =
          text.slice(0, lineStart) +
          line.slice(prefix.length) +
          text.slice(lineEnd);
        delta = -prefix.length;
      } else {
        next = text.slice(0, lineStart) + prefix + line + text.slice(lineEnd);
        delta = prefix.length;
      }
      this.emitValue(next);
      this.$nextTick(() => {
        el.focus();
        el.selectionStart = start + delta;
        el.selectionEnd = start + delta;
      });
    },
    insertVariable() {
      if (!this.selectedVariable) return;
      this.insertAtCursor("{{" + this.selectedVariable + "}}");
      this.selectedVariable = "";
    },
    toggleFullscreen() {
      this.fullscreen = !this.fullscreen;
    },
  },
};
</script>

<style scoped>
.md-editor {
  position: relative;
  width: 100%;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.md-editor--fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  border-radius: 0;
}

.md-editor__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.md-editor__toolbar-group {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 8px;
  margin-right: 8px;
  border-right: 1px solid #dee2e6;
}

.md-editor__toolbar-group:last-child {
  border-right: none;
  margin-right: 0;
}

.md-editor__toolbar-group--right {
  margin-left: auto;
  border-right: none;
  padding-right: 0;
}

.md-editor__btn {
  min-width: 30px;
  height: 28px;
  padding: 0 9px;
  font-size: 13px;
  line-height: 1;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  color: #495057;
}

.md-editor__btn:hover {
  background: #e9ecef;
}

.md-editor__btn.active {
  background: #0d6efd;
  color: #fff;
  border-color: #0d6efd;
}

.md-editor__select {
  height: 28px;
  font-size: 13px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  color: #495057;
  padding: 0 4px;
  max-width: 210px;
}

.md-editor__body {
  position: relative;
  display: flex;
  height: 550px;
}

.md-editor--fullscreen .md-editor__body {
  height: calc(100vh - 43px);
}

.md-editor__suggestions {
  position: absolute;
  z-index: 20;
  min-width: 200px;
  max-width: 320px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.md-editor__suggestions-list {
  list-style: none;
  margin: 0;
  padding: 4px;
  max-height: 200px;
  overflow: auto;
}

.md-editor__suggestion {
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-family: "SFMono-Regular", Consolas, Menlo, monospace;
  font-size: 13px;
  color: #495057;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.md-editor__suggestion:hover,
.md-editor__suggestion.active {
  background: #eef2ff;
  color: #4f46e5;
}

.md-editor__suggestions-hint {
  padding: 6px 10px;
  border-top: 1px solid #f1f3f5;
  font-size: 11px;
  color: #adb5bd;
  background: #f8f9fa;
}

.md-editor__textarea {
  flex: 1;
  border: none;
  outline: none;
  resize: none;
  padding: 12px;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #212529;
  background: #fff;
}

.md-editor__preview {
  flex: 1;
  overflow: auto;
  padding: 12px 16px;
  background: #fff;
  border-left: 1px solid #dee2e6;
  font-size: 14px;
  line-height: 1.7;
  color: #212529;
  word-break: break-word;
}

.md-editor__preview :deep(h1),
.md-editor__preview :deep(h2),
.md-editor__preview :deep(h3),
.md-editor__preview :deep(h4) {
  font-weight: 600;
  margin: 1.2em 0 0.6em;
}

.md-editor__preview :deep(h1) {
  font-size: 1.6em;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.3em;
}

.md-editor__preview :deep(h2) {
  font-size: 1.35em;
}

.md-editor__preview :deep(h3) {
  font-size: 1.15em;
}

.md-editor__preview :deep(a) {
  color: #0d6efd;
}

.md-editor__preview :deep(hr) {
  border: none;
  border-top: 2px solid #dee2e6;
  margin: 20px 0;
}

.md-editor__preview :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 12px 0;
  border: 2px solid #adb5bd;
}

.md-editor__preview :deep(th),
.md-editor__preview :deep(td) {
  border: 1px solid #ced4da;
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
}

.md-editor__preview :deep(th) {
  background: #e9ecef;
  font-weight: 600;
}

.md-editor__preview :deep(tbody tr:nth-child(even)) {
  background: #f8f9fa;
}

.md-editor__preview :deep(.md-variable) {
  display: inline-block;
  padding: 0 5px;
  margin: 0 1px;
  border-radius: 4px;
  background: #eef2ff;
  color: #4f46e5;
  border: 1px solid #c7d2fe;
  font-family: "SFMono-Regular", Consolas, Menlo, monospace;
  font-size: 0.92em;
}

.md-editor__preview :deep(.md-variable--special) {
  display: block;
  margin: 12px 0;
  padding: 14px;
  text-align: center;
  background: #fff7ed;
  border: 1px dashed #fdba74;
  color: #ea580c;
}

.md-editor__preview :deep(.md-container--info) {
  margin: 14px 0;
  padding: 14px 18px;
  background: #e7f1ff;
  border-left: 4px solid #0d6efd;
  border-radius: 6px;
}

.md-editor__preview :deep(.md-container--justify) {
  margin: 16px 0;
  text-align: center;
}

.md-editor__preview :deep(.md-container--justify a) {
  display: inline-block;
  padding: 12px 28px;
  background: #0d6efd;
  color: #fff !important;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
}

.md-editor__preview :deep(.md-container--justify a:hover) {
  background: #0b5ed7;
}
</style>
